import { bool, number, object, oneOfType, string } from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import logger from 'shared/services/logger'
import store from 'shared/store'
import {
  initCrossSellAction,
  initSliderAction,
} from 'shared/store/ducks/events'
import HorizontalSliderSkeleton from 'views/components/organisms/HorizontalSliderSkeleton'
import mountSellerModalContainer from 'views/containers/browser/SellerModalContainer/mountSellerModalContainer'
import SliderComponent from 'views/containers/crosssell/CrosssellSliderHorizontal/SliderComponent'
import { getFinalImageProps } from 'views/containers/crosssell/helpers'
import { fetchGkAirRecommendations } from 'views/containers/crosssell/shared/fetchGkAirRecommendations'
import { getGkAirAdServerData } from 'views/utils/adServerUtils'
import {
  usePageProperties,
  usePublicConfig,
} from 'views/utils/shopAndRuntimeConfigUtils'

const GkAirCategorySliderHorizontal = ({
  cid,
  gkAirArea,
  gkAirServiceId,
  imageMaxWidth,
  imageMinWidth,
  imageSizes,
  showBuyButton,
  title,
  wid,
  ...rest
}) => {
  const [response, setResponse] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const initializedRef = useRef(false)

  const publicConfig = usePublicConfig()
  const { coreShopEnvironment, expa, experiments } = usePageProperties()
  const {
    crosssell: { widgets },
    language,
    locale,
    tenant,
  } = publicConfig

  useEffect(() => {
    let isMounted = true

    const loadRecommendations = async () => {
      try {
        const data = await fetchGkAirRecommendations({
          adServerData: getGkAirAdServerData(experiments),
          cid,
          coreShopEnvironment,
          expa,
          gkAirArea,
          language,
          locale,
          serviceId: gkAirServiceId,
          tenant,
        })

        if (isMounted) {
          setResponse(data)
        }
      } catch (error) {
        logger.warn(`GK Air category fetch failed: ${error}`)
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadRecommendations()

    return () => {
      isMounted = false
    }
  }, [
    cid,
    coreShopEnvironment,
    expa,
    experiments,
    gkAirArea,
    gkAirServiceId,
    language,
    locale,
    tenant,
  ])

  useEffect(() => {
    if (!response?.items?.length || initializedRef.current) {
      return
    }

    initializedRef.current = true

    requestAnimationFrame(() => {
      store.dispatch(initSliderAction())
      store.dispatch(
        initCrossSellAction({ selector: `[data-crosssell-id="${gkAirArea}"]` })
      )
      mountSellerModalContainer()
    })
  }, [gkAirArea, response])

  if (isLoading) {
    return (
      <HorizontalSliderSkeleton
        visibleItemCount={rest.visibleItemCount}
        noSlider={rest.noSlider}
      />
    )
  }

  if (!response?.items?.length) {
    return null
  }

  return (
    <SliderComponent
      items={response.items}
      metadata={response.metadata || {}}
      title={title || response?.widgetdetails?.title}
      wid={wid}
      widgets={widgets}
      gkAirArea={gkAirArea}
      gkAirServiceId={gkAirServiceId}
      publicRuntimeConfig={{
        publicConfig: {
          language,
          tenant,
        },
      }}
      adaptableArrows
      titleIsHtml
      showBuyButton={showBuyButton}
      {...getFinalImageProps(imageMinWidth, imageMaxWidth, imageSizes)}
      {...rest}
    />
  )
}

GkAirCategorySliderHorizontal.propTypes = {
  cid: string.isRequired,
  gkAirArea: string.isRequired,
  gkAirServiceId: string.isRequired,
  imageMinWidth: number,
  imageMaxWidth: number,
  imageSizes: oneOfType([string, object]),
  showBuyButton: bool,
  title: string,
  wid: oneOfType([number, string]).isRequired,
}

GkAirCategorySliderHorizontal.defaultProps = {
  imageMinWidth: null,
  imageMaxWidth: null,
  imageSizes: null,
  showBuyButton: false,
  title: null,
}

export default GkAirCategorySliderHorizontal
